import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchAllocations,
  fetchAllocationById,
  createAllocation,
  updateAllocation,
  deleteAllocation,
} from '@/lib/api/allocations'
import { broadcastInvalidation } from '@/lib/sync'
import type { AllocationFormData, AllocationFilters } from '@/types/allocations'
import { toast } from 'sonner'

/**
 * Query key factory for allocations
 * Provides consistent keys for cache management
 */
export const allocationKeys = {
  all: ['allocations'] as const,
  lists: () => [...allocationKeys.all, 'list'] as const,
  list: (organizationId: string, filters?: AllocationFilters) =>
    [...allocationKeys.lists(), organizationId, filters] as const,
  details: () => [...allocationKeys.all, 'detail'] as const,
  detail: (id: string) => [...allocationKeys.details(), id] as const,
}

/**
 * Fetch all allocations for current organization with optional filtering
 */
export function useAllocations(filters?: AllocationFilters) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: allocationKeys.list(organizationId || '', filters),
    queryFn: () => fetchAllocations(organizationId!, filters),
    enabled: !!organizationId,
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch a single allocation by ID
 */
export function useAllocationById(id: string | undefined) {
  return useQuery({
    queryKey: allocationKeys.detail(id || ''),
    queryFn: () => fetchAllocationById(id!),
    enabled: !!id,
  })
}

/**
 * Create a new allocation
 */
export function useCreateAllocation() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async (data: AllocationFormData) => {
      return createAllocation(currentOrganization!.id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.lists() })
      broadcastInvalidation([allocationKeys.lists()])
      toast.success('Allocation created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create allocation', {
        description: error.message,
      })
    },
  })
}

/**
 * Update an existing allocation
 */
export function useUpdateAllocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<AllocationFormData>
    }) => {
      return updateAllocation(id, data)
    },
    onSuccess: (_, { id }) => {
      const keys = [allocationKeys.lists(), allocationKeys.detail(id)] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Allocation updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update allocation', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete an allocation (soft delete)
 */
export function useDeleteAllocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteAllocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.lists() })
      broadcastInvalidation([allocationKeys.lists()])
      toast.success('Allocation deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete allocation', {
        description: error.message,
      })
    },
  })
}
